import LoadPanel from 'devextreme-react/load-panel';
import { includes } from 'ramda';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Platform } from '../components/loginForm/LoginForm';
import { Content } from '../Content';
import { useAssetClass } from '../contexts/assetClass';
import { AuthProvider, useAuth } from '../contexts/auth';
import { MobileAuthProvider, useMobileAuth } from '../contexts/mobileAuthContext';
import { NavigationProvider } from '../contexts/navigation';
import { RoutingProvider } from '../contexts/routingContext';
import { TerminalAuthProvider, useTerminalAuth } from '../contexts/terminalAuthContext';
import { ThemeProvider } from '../contexts/theme';
import { WindowFocusProvider } from '../contexts/windowFocusContext';
import { useScreenSizeClass } from '../hooks/mediaQuery';
import useQueryParams from '../hooks/useQueryParams';
import { MobileRoutes } from '../MobileRoutes';
import { OutlookPluginTaskCreate } from '../pages/_OutlookPlugin/OutlookPluginTaskCreate';
import { TerminalRoutes } from '../TerminalRoutes';
import { UnauthenticatedContent } from '../UnauthenticatedContent';

function App({ entryUrl }) {
  const { user, loading: loadingUser, setEntryUrl, entryUrl: _entryUrl } = useAuth();
  const { assetClass } = useAssetClass();

  useEffect(() => {
    if (entryUrl === '/') entryUrl = '/home';
    if (setEntryUrl && !user && !_entryUrl)
      setEntryUrl(entryUrl?.includes('login') ? (_entryUrl ? _entryUrl : null) : entryUrl);
  }, [entryUrl]);

  if (loadingUser) return <LoadPanel visible={true} />;
  if (user && assetClass)
    return (
      <RoutingProvider>
        <Content />
      </RoutingProvider>
    );
  return <UnauthenticatedContent path={'/login'} platform={Platform.DESKTOP} />;
}

interface TerminalAppProps {
  entryUrl: string;
  query: string | null;
}

function TerminalApp({ entryUrl, query }: TerminalAppProps) {
  const {
    user,
    loading: loadingUser,
    setEntryUrl,
    entryUrl: _entryUrl,
    terminalMappings,
    terminalMappingRedirectEnabled,
    setAssetIdFromUrl,
    setCapacityCategoryIdArrFromUrl,
    capacityCategoryIdArrFromUrl: capacityCategoryIdArrFromUrlAuth,
  } = useTerminalAuth();
  const { assetClass } = useAssetClass();
  const location = useLocation();
  const queryParams: any = useQueryParams();

  useEffect(() => {
    if (entryUrl === '/terminal') entryUrl = '/terminal/tasks';
    if (setEntryUrl && !_entryUrl)
      setEntryUrl(entryUrl?.includes('terminal/login') ? (_entryUrl ? _entryUrl : null) : entryUrl);
  }, [entryUrl]);

  // V URL je možné předat id assetu - řeší problém, když není nic v TerminalMapping #442
  const assetIdFromUrl: number | null = queryParams.assetId ? Number(queryParams.assetId) : null;

  const parseCapacityCategoryFromUrl = (url: string) => {
    let toReturn: number[] = [];
    const split = url.split('&');
    split.forEach((item) => {
      if (item.includes('capacityCategoryArr')) toReturn.push(Number(item.split('=')[1]));
    });
    return toReturn;
  };

  let capacityCategoryIdArrFromUrl: number[] = [];
  if (query && query.includes('capacityCategoryArr'))
    capacityCategoryIdArrFromUrl = parseCapacityCategoryFromUrl(query);

  useEffect(() => {
    if (assetIdFromUrl && setAssetIdFromUrl) setAssetIdFromUrl(assetIdFromUrl);
    if (
      capacityCategoryIdArrFromUrl.length > 0 &&
      setCapacityCategoryIdArrFromUrl &&
      capacityCategoryIdArrFromUrlAuth?.length === 0
    ) {
      setCapacityCategoryIdArrFromUrl(capacityCategoryIdArrFromUrl);
    }
  });

  if (loadingUser) return <LoadPanel visible={true} />;
  if (user && assetClass) return <TerminalRoutes assetIdFromUrl={assetIdFromUrl} />;

  let path =
    !terminalMappings || terminalMappings.length === 0
      ? '/terminal/login'
      : '/terminal/terminal-mapping-login';

  return (
    <UnauthenticatedContent
      path={path}
      platform={Platform.TERMINAL}
      terminalMappingRedirectEnabled={terminalMappingRedirectEnabled}
    />
  );
}

function MobileApp({ entryUrl }) {
  const { user, loading: loadingUser, setEntryUrl, entryUrl: _entryUrl } = useMobileAuth();
  const { assetClass } = useAssetClass();

  useEffect(() => {
    if (entryUrl === '/mobile') entryUrl = '/mobile/tasks/list';
    if (setEntryUrl && !user && !_entryUrl)
      setEntryUrl(entryUrl?.includes('mobile/login') ? (_entryUrl ? _entryUrl : null) : entryUrl);
  }, [entryUrl]);

  if (loadingUser) return <LoadPanel visible={true} />;
  if (user && assetClass) return <MobileRoutes />;
  return <UnauthenticatedContent path={'/mobile/login'} platform={Platform.MOBILE} />;
}

export const RoutesContainer = () => {
  // const screenSizeClass = useScreenSizeClass();
  const { pathname, search } = useLocation();

  if (includes('/terminal', pathname) && !includes('/admin-terminal-mapping', pathname))
    return (
      <TerminalAuthProvider>
        <NavigationProvider>
          <div className={`app-terminal`}>
            <TerminalApp
              entryUrl={search ? `${pathname}${search}` : pathname}
              query={search ? search : null}
            />
          </div>
        </NavigationProvider>
      </TerminalAuthProvider>
    );

  if (includes('/mobile', pathname))
    return (
      <MobileAuthProvider>
        <NavigationProvider>
          <div className={`app-mobile`}>
            <MobileApp entryUrl={pathname} />
          </div>
        </NavigationProvider>
      </MobileAuthProvider>
    );

  if (includes('/outlook-plugin', pathname))
    return (
      <NavigationProvider>
        <div className={`app`}>
          <OutlookPluginTaskCreate />
        </div>
      </NavigationProvider>
    );

  return (
    <AuthProvider>
      <WindowFocusProvider>
        <NavigationProvider>
          <ThemeProvider>
            <div className={`app`}>
              <App entryUrl={pathname} />
            </div>
          </ThemeProvider>
        </NavigationProvider>
      </WindowFocusProvider>
    </AuthProvider>
  );
};
